import React, { useState, useEffect, useCallback } from 'react';
import { useDashboardStats } from '../hooks/useDashboardStats.ts';
import { metricsService } from '../services/metricsService.ts';
import { supabase } from '../services/supabase.ts';
import StatCard from '../components/dashboard/StatCard.tsx';
import RentCollection from '../components/dashboard/RentCollection.tsx';
import PropertyStatus from '../components/dashboard/PropertyStatus.tsx';
import PaymentHistory from '../components/dashboard/PaymentHistory.tsx';
import Alerts from '../components/dashboard/PaymentAlerts.tsx';  // Renamed import
import LoadingSpinner from '../components/shared/LoadingSpinner.tsx';
import ErrorMessage from '../components/shared/ErrorMessage.tsx';
import { Alert } from '../types/alert.ts';
import '../styles/Dashboard.css';
import '../styles/PaymentAlerts.css';
import OutstandingSettlements from '../components/dashboard/OutstandingSettlements.tsx';
import { formatCurrency } from '../utils/formatters.ts';

const formatErrorMessage = (error: string | Error | null): string => {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return 'An error occurred';
};

const Dashboard: React.FC = () => {
  const { stats, loading: statsLoading, error: statsError } = useDashboardStats();
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [error, setError] = useState<string | Error | null>(null);
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<any>(null);
  const [metricsLoading, setMetricsLoading] = useState(true);

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('bills')
        .select(`
          *,
          tenants!inner(id, full_name, payment_due_date),
          properties!inner(id, name)
        `)
        .in('status', ['Unpaid', 'Partial']) // Update to use new statuses
        .order('due_date', { ascending: true });

      if (error) throw error;
      setAlerts(data || []);
      setError(null);
    } catch (error: any) {
      console.error('Error fetching alerts:', error);
      setError(error?.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMetrics = async () => {
    setMetricsLoading(true);
    try {
      const data = await metricsService.getDashboardMetrics();
      setMetrics(data);
    } catch (err) {
      console.error('Error loading metrics:', err);
    } finally {
      setMetricsLoading(false);
    }
  };

  useEffect(() => {
    Promise.all([fetchAlerts(), loadMetrics()]);
  }, [fetchAlerts]);

  if (statsLoading || loading || metricsLoading) return <LoadingSpinner />;
  if (statsError || error) return <ErrorMessage message={formatErrorMessage(statsError || error)} />;
  if (!stats) return <ErrorMessage message="No data available" />;

  return (
    <div className="dashboard">
      <h1>Dashboard</h1>
      
      <Alerts paymentAlerts={alerts} leaseAlerts={stats.upcomingLeaseEndings} />
      
      <div className="stats-grid">
        <StatCard
          title="Properties"
          value={`${stats.occupiedProperties}/${stats.totalProperties}`}
          subtitle={`${Math.round((stats.occupiedProperties / stats.totalProperties) * 100)}% Occupied`}
          icon="🏠"
        />
        <StatCard
          title="Monthly Collection"
          value={formatCurrency(stats.monthlyCollection)}
          subtitle={`${metrics?.collectionRate?.toFixed(1) || 0}% Collection Rate`}
          icon="💰"
        />
      </div>

      <div className="metrics-grid">
        <StatCard
          title="Avg. Tenant Duration"
          value={`${metrics?.averageTenancyMonths} months`}
          icon="📅"
        />
        <StatCard
          title="Yearly Maintenance"
          value={formatCurrency(metrics?.yearlyMaintenance || 0)}
          icon="🔧"
        />
      </div>

      <div className="charts-grid">
        <RentCollection data={stats.rentCollection} />
        <PropertyStatus data={stats.propertyStatus} />
      </div>

      <OutstandingSettlements />
      
      <PaymentHistory data={stats.recentPayments} />
    </div>
  );
};

export default Dashboard;
